import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { ConversationMessage } from "@redotech/redo-model/conversation";
import {
  allowSupportAiCopilot,
  shouldAutoGenerateAiResponses,
  Team,
} from "@redotech/redo-model/team";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import Quill from "quill";
import { useCallback, useEffect, useMemo, useState } from "react";

interface UseCopilotSuggestionParams {
  conversationId: string;
  messageReplyingTo: ConversationMessage;
  team: Team;
  quill: Quill | null;
  onConversationUpdate: (
    updater: (messages: ConversationMessage[]) => ConversationMessage[],
  ) => void;
}

interface UseCopilotSuggestionResult {
  suggestedReply: string | null;
  isGenerating: boolean;
  error: Error | null;
  handleAccept: () => void;
  handleDecline: () => void;
  handleRefresh: () => void;
  setSuggestedReply: (text: string) => void;
}

/**
 * Custom hook to manage AI copilot suggestions for conversation replies.
 * Handles generation, caching, acceptance, and dismissal of suggestions.
 */
export function useCopilotSuggestion({
  conversationId,
  messageReplyingTo,
  team,
  quill,
  onConversationUpdate,
}: UseCopilotSuggestionParams): UseCopilotSuggestionResult {
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [dismissed, setDismissed] = useState(false);
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState<
    string | undefined
  >();

  const canUseAi = useMemo(() => allowSupportAiCopilot(team), [team]);
  const shouldAutoGenerate = useMemo(
    () => shouldAutoGenerateAiResponses(team),
    [team],
  );

  // Derive suggestion from server-side aiGenerations
  const suggestedReply = useMemo(() => {
    return messageReplyingTo.aiGenerations?.[0]?.text ?? null;
  }, [messageReplyingTo.aiGenerations]);

  const generateMutation = useMutation({
    mutationFn: async ({
      conversationId,
      prevMessageId,
      overwrite,
    }: {
      conversationId: string;
      prevMessageId: string;
      overwrite: boolean;
    }) => {
      return await supportRpcClient.generateCopilotResponse({
        conversationId,
        prevMessageId,
        overwrite,
      });
    },
    onSuccess: (result) => {
      if (result?.text) {
        // Optimistically update the conversation messages
        // This will automatically update the derived suggestedReply
        onConversationUpdate((messages) =>
          messages.map((msg) =>
            msg._id === messageReplyingTo._id
              ? {
                  ...msg,
                  aiGenerations: [
                    {
                      text: result.text,
                    },
                  ],
                }
              : msg,
          ),
        );
      }
    },
    onError: (error) => {
      console.error("Failed to generate copilot suggestion:", error);
      toast("Failed to generate AI suggestion. Please try again.", {
        variant: "error",
      });
    },
  });

  // Reset dismissed state when message changes
  useEffect(() => {
    setDismissed(false);
    setLastProcessedMessageId(undefined);
  }, [conversationId, messageReplyingTo._id]);

  // Stable callback for triggering generation
  const triggerGeneration = useCallback(
    (prevMessageId: string) => {
      void generateMutation.mutateAsync({
        conversationId,
        prevMessageId,
        overwrite: false,
      });
    },
    [conversationId, generateMutation],
  );

  // Automatically generate copilot suggestion when conditions are met
  useEffect(() => {
    if (
      !canUseAi ||
      !shouldAutoGenerate ||
      !quill ||
      generateMutation.isPending
    ) {
      return;
    }

    // Don't generate if server already has aiGenerations for this message
    if (messageReplyingTo.aiGenerations?.[0]?.text) {
      return;
    }

    // Don't generate if user has started typing
    if (quill.getText().trim().length > 0) {
      return;
    }

    // Only generate if replying to a customer message
    if (messageReplyingTo.type !== "customer") {
      return;
    }

    const replyingToId = messageReplyingTo._id;

    // Skip if we already processed this message
    if (lastProcessedMessageId === replyingToId) {
      return;
    }

    // Generate copilot response automatically (don't overwrite if cached)
    triggerGeneration(replyingToId);

    // Mark this message as processed
    setLastProcessedMessageId(replyingToId);
  }, [
    conversationId,
    messageReplyingTo._id,
    messageReplyingTo.type,
    messageReplyingTo.aiGenerations,
    quill,
    lastProcessedMessageId,
    canUseAi,
    shouldAutoGenerate,
    generateMutation.isPending,
    triggerGeneration,
  ]);

  const handleAccept = useHandler(() => {
    if (suggestedReply && quill) {
      amplitude.logEvent("accept-copilotSuggestion", {
        conversationId,
      });
      quill.setText(suggestedReply);
      setDismissed(true);
    }
  });

  const handleDecline = useHandler(() => {
    amplitude.logEvent("decline-copilotSuggestion", {
      conversationId,
    });
    setDismissed(true);
  });

  const handleRefresh = useHandler(() => {
    amplitude.logEvent("refresh-copilotSuggestion", {
      conversationId,
    });
    setDismissed(false);
    void generateMutation.mutateAsync({
      conversationId,
      prevMessageId: messageReplyingTo._id,
      overwrite: true,
    });
  });

  return {
    suggestedReply: dismissed ? null : suggestedReply,
    isGenerating: generateMutation.isPending,
    error: generateMutation.error,
    handleAccept,
    handleDecline,
    handleRefresh,
    setSuggestedReply: (text: string) => {
      setDismissed(false);
      onConversationUpdate((messages) =>
        messages.map((msg) =>
          msg._id === messageReplyingTo._id
            ? {
                ...msg,
                aiGenerations: [{ text }],
              }
            : msg,
        ),
      );
    },
  };
}
